import type {IconProps} from '@lightricks/react-design-system/dist/components/icon/IconProps';
import translate from '@/utils/translate';
import {
  CampaignMetricsTotalsByNetwork,
  CampaignMetricsAveragesByNetwork,
  SocialStreamTab,
} from '@/types/socialMediaPost';
import {getEngagementRateFormula} from '../organic-posts-table/engagementRateFormula';

export interface StatConfig {
  key: string;
  label: string;
  iconName: IconProps['name'];
  getValue: (
    stats: CampaignMetricsTotalsByNetwork | CampaignMetricsAveragesByNetwork
  ) => number | null | undefined;
  format?: 'number' | 'percentage' | 'currency';
  tooltip?: string;
}

function getStatIconName(key: string): IconProps['name'] {
  const normalizedKey = key.replace(/PerPost$/, '');

  switch (normalizedKey) {
    case 'creatorCount':
    case 'followerCount':
      return 'Navigation-Profile';
    case 'impressionCount':
      return 'Features-Eye';
    case 'commentCount':
    case 'replyCount':
      return 'Actions-Comment1';
    case 'likeCount':
      return 'Actions-Like';
    case 'videoAggregatedPlaysCount':
    case 'viewCount':
      return 'Features-Play';
    case 'totalInteractionsCount':
    case 'reachCount':
    case 'engagementRate':
    case 'emv':
      return 'Features-Trending';
    default:
      return 'Features-Trending';
  }
}

function createTotalStatConfig(
  key: keyof CampaignMetricsTotalsByNetwork,
  labelKey: string,
  format?: 'number' | 'percentage' | 'currency',
  getValue?: (
    stats: CampaignMetricsTotalsByNetwork | CampaignMetricsAveragesByNetwork
  ) => number | null | undefined
): StatConfig {
  return {
    key,
    label: translate(labelKey),
    iconName: getStatIconName(key),
    getValue: getValue || ((s) => (s as CampaignMetricsTotalsByNetwork)[key]),
    format,
  };
}

function createAverageStatConfig(
  key: keyof CampaignMetricsAveragesByNetwork,
  labelKey: string,
  format?: 'number' | 'percentage',
  getValue?: (
    stats: CampaignMetricsAveragesByNetwork | CampaignMetricsTotalsByNetwork
  ) => number | undefined
): StatConfig {
  return {
    key,
    label: translate(labelKey),
    iconName: getStatIconName(key),
    getValue: getValue || ((s) => (s as CampaignMetricsAveragesByNetwork)[key]),
    format,
  };
}

function isTikTok(network?: string | null): boolean {
  return network === 'tiktok_v2';
}

export function getTotalStatsForStream(
  socialStream: SocialStreamTab,
  network?: string | null,
  {showEmv = false, emvTooltip}: {showEmv?: boolean; emvTooltip?: string} = {}
): StatConfig[] {
  const reachCount = createTotalStatConfig('reachCount', 'Reach');
  const totalInteractionsCount = createTotalStatConfig(
    'totalInteractionsCount',
    'Total Interactions'
  );
  const creatorCount = createTotalStatConfig('creatorCount', 'Creators');
  const followerCount = createTotalStatConfig('followerCount', 'Followers');
  // TikTok has no distinct "plays" metric (videoAggregatedPlaysCount is 0), so
  // relabel to Views sourced from viewCount.
  const playsCount = isTikTok(network)
    ? createTotalStatConfig('viewCount', 'Views')
    : createTotalStatConfig('videoAggregatedPlaysCount', 'Plays');
  const impressionCount = createTotalStatConfig(
    'impressionCount',
    'Impressions'
  );
  const replyCount = createTotalStatConfig('replyCount', 'Replies');
  const engagementRateFormula = getEngagementRateFormula(network, socialStream);
  const engagementRate = {
    ...createTotalStatConfig('engagementRate', 'Engagement Rate', 'percentage'),
    ...(engagementRateFormula
      ? {tooltip: translate(engagementRateFormula)}
      : {}),
  };
  const emv = {
    ...createTotalStatConfig('emv', 'EMV', 'currency'),
    ...(emvTooltip ? {tooltip: emvTooltip} : {}),
  };

  // TikTok uses this single card set for every tab; only the data sources
  // (handled above) differ from Instagram's.
  if (isTikTok(network)) {
    const tiktokStats = [
      totalInteractionsCount,
      creatorCount,
      followerCount,
      playsCount,
      engagementRate,
    ];
    return showEmv ? [...tiktokStats, emv] : tiktokStats;
  }

  const streamSpecificStats: Record<SocialStreamTab, StatConfig[]> = {
    feed: [creatorCount, followerCount, impressionCount, engagementRate],
    reels: [
      totalInteractionsCount,
      creatorCount,
      followerCount,
      playsCount,
      engagementRate,
    ],
    story: [impressionCount, reachCount, replyCount],
    all: [totalInteractionsCount, creatorCount, followerCount, playsCount],
  };

  const stats = streamSpecificStats[socialStream];
  return showEmv ? [...stats, emv] : stats;
}

export function getAverageStatsForStream(
  socialStream: SocialStreamTab,
  network?: string | null
): StatConfig[] {
  const followerCountPerPost = createAverageStatConfig(
    'followerCountPerPost',
    'Followers'
  );
  const commentCountPerPost = createAverageStatConfig(
    'commentCountPerPost',
    'Comments'
  );
  const likeCountPerPost = createAverageStatConfig('likeCountPerPost', 'Likes');
  const reachCountPerPost = createAverageStatConfig(
    'reachCountPerPost',
    'Reach'
  );
  const videoAggregatedPlaysCountPerPost = isTikTok(network)
    ? createAverageStatConfig('viewCountPerPost', 'Views')
    : createAverageStatConfig('videoAggregatedPlaysCountPerPost', 'Plays');
  const impressionCountPerPost = createAverageStatConfig(
    'impressionCountPerPost',
    'Impressions'
  );
  const totalInteractionsCountPerPost = createAverageStatConfig(
    'totalInteractionsCountPerPost',
    'Total Interactions'
  );
  const replyCountPerPost = createAverageStatConfig(
    'replyCountPerPost',
    'Replies'
  );

  // TikTok uses this single card set for every tab; only the data sources
  // (handled above) differ from Instagram's.
  if (isTikTok(network)) {
    return [
      totalInteractionsCountPerPost,
      followerCountPerPost,
      videoAggregatedPlaysCountPerPost,
    ];
  }

  const streamSpecificStats: Record<SocialStreamTab, StatConfig[]> = {
    feed: [likeCountPerPost, commentCountPerPost],
    reels: [
      totalInteractionsCountPerPost,
      followerCountPerPost,
      videoAggregatedPlaysCountPerPost,
    ],
    story: [impressionCountPerPost, reachCountPerPost, replyCountPerPost],
    all: [
      totalInteractionsCountPerPost,
      followerCountPerPost,
      videoAggregatedPlaysCountPerPost,
    ],
  };

  return streamSpecificStats[socialStream];
}
